import { nextTick } from "vue";

/**
 * Reusable retry logic for initializing custom scrollbars or similar async DOM-dependent logic.
 * @param {Function} initializer - Function to call with (containerRef, contentRef, thumb, scroller) when ready.
 * @param {Ref} customScroller - Ref to the custom scroller component.
 * @param {Ref} containerRef - Ref to the container DOM element.
 * @param {Ref} contentRef - Ref to the content DOM element.
 * @param {number} [retries=3] - Number of attempts.
 * @param {number} [delay=100] - Delay between attempts (ms).
 * @returns {Function} - An async function to perform the retry logic.
 */
export function useInitializeWithRetry(
  initializer,
  customScroller,
  containerRef,
  contentRef,
  retries = 3,
  delay = 100
) {
  return async () => {
    for (let i = 0; i < retries; i++) {
      await nextTick();
      const thumb = customScroller.value?.thumbRef;
      const scroller = customScroller.value?.scrollerRef;
      if (thumb && scroller) {
        initializer(containerRef, contentRef, thumb, scroller);
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
    console.error(
      "Failed to initialize scrollbar after retries: thumb or scroller ref is still undefined"
    );
  };
}
